import type { MouseEvent } from 'react';

import { CardSelectButton } from '../CardSelectButton';
import { FromPriceDisplay } from '../PriceDisplay';
import { selectOnCardClick } from '../../utils/cardClick';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  CardWrapper,
  EditSearchButton,
  Label,
  Name,
  PriceWrapper,
  RightSection,
  TextContent,
  Thumbnail,
  ThumbnailImage,
  ThumbnailPlaceholder,
  UnavailableBody,
  UnavailableDivider,
  UnavailableHeader,
  UnavailableText,
  UnavailableTextWrapper,
} from './ListItemCard.styles';
import { THUMBNAIL_IMAGE_SIZING } from '../EntityImage';
import { PlusIcon } from '../../icons/PlusIcon';
import type { ListItemCardProps } from './ListItemCard.types';

export function ListItemCard({
  name,
  label,
  image,
  hideImage,
  price,
  buttonLabel = 'Select',
  onSelect,
  unavailable,
  unavailableLabel = 'Unavailable',
  editSearchLabel = 'Edit search',
  onEditSearch,
  actions,
  stackedRight,
  stackedBelow,
}: ListItemCardProps) {
  const clickable = Boolean(onSelect) && !unavailable;

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect?.();
  };

  const priceDisplay = price ? (
    <FromPriceDisplay
      amount={price.amount}
      currencyCode={price.currencyCode}
      label={price.label}
      discount={price.discount}
      locale={price.locale}
      size="base"
    />
  ) : null;

  const textContent = (
    <TextContent>
      <Name>
        {clickable && actions && onSelect ? (
          <CardSelectButton onSelect={onSelect}>{name}</CardSelectButton>
        ) : (
          name
        )}
      </Name>
      {label && <Label>{label}</Label>}
    </TextContent>
  );

  const thumbnail = !hideImage ? (
    <Thumbnail>
      {image?.src ? (
        <ThumbnailImage src={image.src} alt={image.alt ?? name} sizing={THUMBNAIL_IMAGE_SIZING} />
      ) : (
        <ThumbnailPlaceholder data-testid="thumbnail-placeholder" />
      )}
    </Thumbnail>
  ) : null;

  return (
    <CardWrapper
      $unavailable={unavailable}
      $clickable={clickable}
      $stackedBelow={stackedBelow}
      onClick={clickable && onSelect ? selectOnCardClick(onSelect) : undefined}
      data-testid="list-item-card"
    >
      {unavailable ? (
        <UnavailableBody>
          <UnavailableHeader>
            {thumbnail}
            {textContent}
          </UnavailableHeader>
          <UnavailableTextWrapper>
            <UnavailableText>{unavailableLabel}</UnavailableText>
            {onEditSearch && (
              <>
                <UnavailableDivider aria-hidden />
                <EditSearchButton type="button" onClick={onEditSearch}>
                  {editSearchLabel}
                </EditSearchButton>
              </>
            )}
          </UnavailableTextWrapper>
        </UnavailableBody>
      ) : (
        <>
          {thumbnail}
          {textContent}
          <RightSection $stacked={stackedRight} $stackedBelow={stackedBelow}>
            {priceDisplay && <PriceWrapper>{priceDisplay}</PriceWrapper>}
            {actions
              ? actions
              : onSelect && (
                  <ActionButton onClick={handleActionClick} aria-label={`${buttonLabel}, ${name}`}>
                    <ActionButtonLabel>{buttonLabel}</ActionButtonLabel>
                    <ActionButtonIcon aria-hidden>
                      <PlusIcon size={13} />
                    </ActionButtonIcon>
                  </ActionButton>
                )}
          </RightSection>
        </>
      )}
    </CardWrapper>
  );
}
